import {
  createContext,
  type MouseEvent,
  type ReactNode,
  useContext,
  useEffect,
  useMemo,
  useState
} from "react";
import type { RouteKey } from "./types";

const routes: Record<string, RouteKey> = {
  "/": "home",
  "/about": "about",
  "/people": "people",
  "/alumni": "alumni",
  "/research": "research",
  "/projects": "projects",
  "/publications": "publications",
  "/news": "news",
  "/contact": "contact"
};

interface RouterValue {
  path: string;
  route: RouteKey;
  navigate: (to: string) => void;
}

const RouterContext = createContext<RouterValue | null>(null);

function cleanPath(pathname: string) {
  if (pathname === "/") return pathname;
  return pathname.replace(/\/+$/, "");
}

export function RouterProvider({ children }: { children: ReactNode }) {
  const [path, setPath] = useState(() => cleanPath(window.location.pathname));

  useEffect(() => {
    const onPopState = () => setPath(cleanPath(window.location.pathname));
    window.addEventListener("popstate", onPopState);
    return () => window.removeEventListener("popstate", onPopState);
  }, []);

  const value = useMemo<RouterValue>(
    () => ({
      path,
      route: routes[path] ?? "not-found",
      navigate(to) {
        const nextPath = cleanPath(to.split("?")[0] || "/");
        window.history.pushState({}, "", to);
        setPath(nextPath);
        window.scrollTo({ top: 0, behavior: "instant" });
      }
    }),
    [path]
  );

  return <RouterContext.Provider value={value}>{children}</RouterContext.Provider>;
}

export function useRouter() {
  const value = useContext(RouterContext);
  if (!value) throw new Error("useRouter must be used inside RouterProvider");
  return value;
}

export function SiteLink({
  to,
  children,
  className,
  onNavigate,
  "aria-current": ariaCurrent
}: {
  to: string;
  children: ReactNode;
  className?: string;
  onNavigate?: () => void;
  "aria-current"?: "page";
}) {
  const { navigate } = useRouter();

  function handleClick(event: MouseEvent<HTMLAnchorElement>) {
    if (
      event.defaultPrevented ||
      event.button !== 0 ||
      event.metaKey ||
      event.ctrlKey ||
      event.shiftKey ||
      event.altKey
    ) {
      return;
    }
    event.preventDefault();
    navigate(to);
    onNavigate?.();
  }

  return (
    <a href={to} className={className} onClick={handleClick} aria-current={ariaCurrent}>
      {children}
    </a>
  );
}
